import type { AccessRuleEntitlementContext } from "../models";
import type { AccessRuleMultiEntitlementContext } from "../models";
import type { LiftAccessRuleSaveResult } from "../../models";
import { isRetryableSaveError } from "../saveErrorCodes";
import { entitlementContextKey } from "./models";
import type {
  BulkFailureIdentity,
  BulkSaveFailure,
} from "./BulkSaveFailuresView";

/**
 * A bulk add run and everything learned about it across the first attempt and any
 * retries. Keyed on the entitlement contexts sent rather than on rule sets: the rule
 * sets don't exist yet, so the context is the only identity an add has.
 */
export type BulkAddRun = {
  /** Every context the run set out to add, so anything it never reached is still known. */
  contexts: AccessRuleEntitlementContext[];
  /** Display columns per context, for the outcome grid. */
  identities: Map<string, BulkFailureIdentity>;
  /**
   * Latest outcome per context. Keyed, not appended: a retry only reports on what it
   * re-sent, so counts are recalculated per rule set rather than accumulated.
   */
  outcomes: Map<string, LiftAccessRuleSaveResult>;
  /** The user stopped the run. Nothing failed on its own account. */
  cancelled: boolean;
};

/** Contexts no attempt has reached — the run stopped or was cancelled before them. */
export const notAttemptedAddContexts = (
  run: BulkAddRun,
): AccessRuleEntitlementContext[] =>
  run.contexts.filter(
    (context) => !run.outcomes.has(entitlementContextKey(context)),
  );

/** Failed rule sets as the outcome grid shows them. */
export const toAddFailures = (run: BulkAddRun): BulkSaveFailure[] =>
  [...run.outcomes.entries()]
    .filter(([, result]) => !result.success)
    .map(([key, result]): BulkSaveFailure | undefined => {
      const ruleSet = run.identities.get(key);
      // Dropped rather than rendered with blank identifying columns.
      if (!ruleSet) return undefined;
      return {
        ruleSet,
        errorStatus: result.errorStatus,
        errorCode: result.errorCode,
        errorMessage: result.errorMessage,
      };
    })
    .filter((failure): failure is BulkSaveFailure => !!failure);

/**
 * Everything worth sending again: the transient failures, plus anything no attempt
 * reached. The run stops on its first request that failed outright, so most of a large
 * run can end up unattempted rather than failed, and retrying only the failures would
 * strand the rest.
 *
 * Wider than the delete flow's equivalent because re-sending an add is safe: successes
 * are never re-sent, and the API answers a duplicate with RuleAlreadyExists rather than
 * doubling the rules.
 */
export const retryableAddContexts = (
  run: BulkAddRun,
): AccessRuleEntitlementContext[] => {
  const retryableKeys = new Set(
    [...run.outcomes.entries()]
      .filter(([, result]) => !result.success && isRetryableSaveError(result))
      .map(([key]) => key),
  );

  return run.contexts.filter((context) => {
    const key = entitlementContextKey(context);
    // No outcome at all = never attempted, so nothing rules it out.
    return retryableKeys.has(key) || !run.outcomes.has(key);
  });
};

export const addSuccessCount = (run: BulkAddRun): number =>
  [...run.outcomes.values()].filter((outcome) => outcome.success).length;

export const addFailureCount = (run: BulkAddRun): number =>
  [...run.outcomes.values()].filter((outcome) => !outcome.success).length;

/**
 * Back into the multi contexts the add endpoint takes, one per scope, so a retry
 * sends the same request shape as the first attempt.
 */
export const groupContextsForAdd = (
  contexts: AccessRuleEntitlementContext[],
): AccessRuleMultiEntitlementContext[] => {
  const byScope = new Map<string, AccessRuleMultiEntitlementContext>();

  for (const context of contexts) {
    const scopeKey = [
      context.FulfillmentSeasonId,
      context.LiftAccessRuleScopeCategory,
      context.LiftAccessRuleScopeEntityId,
    ].join(":");

    const existing = byScope.get(scopeKey);
    if (existing) {
      existing.EntitlementTypeIds.push(context.EntitlementTypeId);
      continue;
    }

    byScope.set(scopeKey, {
      EntitlementTypeIds: [context.EntitlementTypeId],
      FulfillmentSeasonId: context.FulfillmentSeasonId,
      LiftAccessRuleScopeCategory: context.LiftAccessRuleScopeCategory,
      LiftAccessRuleScopeEntityId: context.LiftAccessRuleScopeEntityId,
    });
  }

  return [...byScope.values()];
};
